import { useSyncExternalStore } from 'react'

/* Cookie consent. Nothing that sets cookies or identifies visitors runs until the visitor accepts.
   The choice is kept in localStorage (strictly necessary storage, so it needs no consent itself).
   While no such script is configured the site asks nothing: no banner, no "Cookie settings". */

type ConsentedScript = { id: string; src: string; data?: Record<string, string> }

/**
 * Scripts that set cookies or identify visitors. Each loads only after the visitor accepts.
 * Leadsy (visitor identification) is switched off for now. To bring it back, add
 * `{ id: 'vtag-ai-js', src: 'https://r2.leadsy.ai/tag.js', data: { pid: 'c21C1Mg8c0ODhItM', version: '062024' } }`,
 * restore its wording in the banner and the privacy policy, and bump `consentVersion`.
 */
const consentedScripts: ConsentedScript[] = []

export const needsConsent = consentedScripts.length > 0

export type ConsentChoice = 'accepted' | 'declined'

type ConsentState = {
  /** False on the server and while hydrating, so nothing renders until the stored choice is known. */
  ready: boolean
  choice: ConsentChoice | null
  /** The visitor asked to see the choice again (footer "Cookie settings"). */
  reviewing: boolean
}

const storageKey = 'ua-consent'
/** Bump when what visitors consent to changes, so everyone is asked again. */
const consentVersion = 1

const serverState: ConsentState = { ready: false, choice: null, reviewing: false }
const listeners = new Set<() => void>()
let state: ConsentState | undefined

const readStoredChoice = (): ConsentChoice | null => {
  try {
    const stored = JSON.parse(window.localStorage.getItem(storageKey) ?? 'null') as { choice?: string; version?: number } | null
    if (stored?.version !== consentVersion) return null
    return stored.choice === 'accepted' || stored.choice === 'declined' ? stored.choice : null
  } catch {
    return null
  }
}

const getState = () => {
  state ??= { ready: true, choice: readStoredChoice(), reviewing: false }
  return state
}

const update = (next: Partial<ConsentState>) => {
  state = { ...getState(), ...next }
  listeners.forEach((listener) => listener())
}

const subscribe = (listener: () => void) => {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

export const useConsent = () => useSyncExternalStore(subscribe, getState, () => serverState)

export const loadConsentedScripts = () => {
  for (const { id, src, data = {} } of consentedScripts) {
    if (document.getElementById(id)) continue
    const script = document.createElement('script')
    script.id = id
    script.async = true
    script.src = src
    Object.assign(script.dataset, data)
    document.head.appendChild(script)
  }
}

export const chooseConsent = (choice: ConsentChoice) => {
  const previous = getState().choice
  try {
    window.localStorage.setItem(storageKey, JSON.stringify({ choice, version: consentVersion, at: new Date().toISOString() }))
  } catch {
    // Storage unavailable: the choice holds for this visit only.
  }
  update({ choice, reviewing: false })
  if (choice === 'accepted') loadConsentedScripts()
  // A script that already ran cannot be unloaded: start a fresh page without it.
  else if (previous === 'accepted') window.location.reload()
}

export const reviewConsent = () => update({ reviewing: true })
